import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Row,
  Col,
  Input,
  Label,
  FormGroup,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Spinner,
  CustomInput,
  InputGroup,
  InputGroupText,
  InputGroupAddon,
} from "reactstrap";
import {
  fetchAllSchedules,
  updateScheduleCoach,
} from "../Bookings/service/bookingService";
import { getData } from "RestControllers/Controller";
import { getTimeSlots } from "../../../api/api";
import toast, { Toaster } from "react-hot-toast";
import ReactLoading from "react-loading";
import { overlayStyles, loaderContainerStyles } from "styles/commonStyles";
import { useSede } from "context/SedeContext";
import useSedeRefresh from "hooks/useSedeRefresh";
import TestCoachesEndpoint from "components/TestCoachesEndpoint";

const diasSemana = {
  monday: "Lunes",
  tuesday: "Martes",
  wednesday: "Miércoles",
  thursday: "Jueves",
  friday: "Viernes",
  saturday: "Sábado",
  sunday: "Domingo"
};

const dayMapping = {
  MON: "monday",
  TUE: "tuesday",
  WED: "wednesday",
  THU: "thursday",
  FRI: "friday",
  SAT: "saturday",
  SUN: "sunday"
};

const classTypeColors = {
  "Pilates": "#e74c3c",
  "Yoga": "#3498db",
  "Fitness": "#2ecc71",
  "Danza": "#f39c12",
  "Spinning": "#9b59b6",
  "Crossfit": "#e67e22"
};

const asList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeTimeSlot = (value) => {
  if (value == null || value === "") return value;
  const str = String(value);
  return str.length >= 5 ? str.slice(0, 5) : str;
};

/** Merge API TimeSlots with distinct times already present on schedules (fallback for new times). */
const mergeTimeSlots = (apiSlots, schedules) => {
  const byValue = new Map();

  (apiSlots || []).forEach((slot) => {
    const value = normalizeTimeSlot(slot.time_slot_value || slot.start_time);
    if (!value) return;
    byValue.set(value, {
      id: slot.id,
      time_slot_value: value,
      time_slot_display: slot.time_slot_display || value,
      start_time: slot.start_time,
    });
  });

  (schedules || []).forEach((schedule) => {
    const value = normalizeTimeSlot(schedule.time_slot);
    if (!value || byValue.has(value)) return;
    byValue.set(value, {
      id: `from-schedule-${value}`,
      time_slot_value: value,
      time_slot_display: value,
    });
  });

  return Array.from(byValue.values()).sort((a, b) =>
    String(a.time_slot_value).localeCompare(String(b.time_slot_value))
  );
};

const fetchCoaches = async (sedeParams = {}, sedeHeaders = {}) => {
  try {
    const baseUrl = "https://revive-back.vercel.app/api/";
    const res = await getData(
      "GET",
      baseUrl + "accounts/users/coaches/",
      sedeParams,
      {},
      sedeHeaders
    );
    return asList(res.data);
  } catch (error) {
    console.error("Error fetching coaches:", error);
    throw error;
  }
};

const fetchClassTypes = async (sedeParams = {}, sedeHeaders = {}) => {
  try {
    const baseUrl = "https://revive-back.vercel.app/api/";
    const res = await getData(
      "GET",
      baseUrl + "studio/class-types/",
      sedeParams,
      {},
      sedeHeaders
    );
    return asList(res.data);
  } catch (error) {
    console.error("Error fetching class types:", error);
    throw error;
  }
};

const createScheduleAPI = async (payload, sedeParams = {}, sedeHeaders = {}) => {
  try {
    const baseUrl = "https://revive-back.vercel.app/api/";
    const url = `${baseUrl}studio/schedules/`;
    return await getData("POST", url, sedeParams, payload, sedeHeaders);
  } catch (error) {
    console.error("Error creating schedule:", error);
    throw error;
  }
};

const CoachScheduleManager = () => {
  const {
    addSedeParams,
    addSedeHeaders,
    selectedSedes,
    getSedeId,
    getSedeIds,
    availableSedes,
  } = useSede();
  const [schedules, setSchedules] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [coaches, setCoaches] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedClassType, setSelectedClassType] = useState("");
  const [selectedCoach, setSelectedCoach] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSchedules, setSelectedSchedules] = useState([]);
  const [bulkCoach, setBulkCoach] = useState("");
  const [bulkCapacity, setBulkCapacity] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    day: "",
    time_slot: "",
    class_type_id: "",
    coach: "",
    capacity: 1,
    is_individual: false
  });
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [stats, setStats] = useState({
    totalSchedules: 0,
    totalCoaches: 0,
    schedulesWithCoaches: 0,
    schedulesWithoutCoaches: 0
  });
  const [error, setError] = useState(null);

  const resolveSedeId = () => {
    const single = getSedeId();
    if (single) return single;
    const ids = typeof getSedeIds === "function" ? getSedeIds() : [];
    if (ids?.length) return ids[0];
    if (selectedSedes?.length) return selectedSedes[0].id;
    if (availableSedes?.length) return availableSedes[0].id;
    const envDefault = process.env.REACT_APP_DEFAULT_SEDE;
    return envDefault ? Number(envDefault) || envDefault : null;
  };

  const buildSchedulePayload = (current, overrides = {}) => ({
    day: current.day,
    class_type_id: current.class_type?.id,
    coach:
      overrides.coach !== undefined
        ? overrides.coach
        : current.coach ?? null,
    time_slot: normalizeTimeSlot(
      overrides.time_slot !== undefined ? overrides.time_slot : current.time_slot
    ),
    capacity:
      overrides.capacity !== undefined ? overrides.capacity : current.capacity,
  });

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      let allSchedules = [];
      let coachList = [];
      let classTypesList = [];
      let apiTimeSlots = [];

      const sedeParams = addSedeParams({});
      const sedeHeaders = addSedeHeaders({});
      const sedeId = resolveSedeId();

      try {
        allSchedules = asList(await fetchAllSchedules(sedeParams, sedeHeaders));
      } catch (scheduleError) {
        console.error("Error loading schedules:", scheduleError);
        if (scheduleError.response?.status >= 400) {
          toast.error(
            `Error al cargar horarios: ${scheduleError.message || "Error del servidor"}`
          );
        }
      }

      try {
        coachList = await fetchCoaches(sedeParams, sedeHeaders);
      } catch (coachError) {
        console.error("Error loading coaches:", coachError);
        if (coachError.response?.status >= 400) {
          toast.error(
            `Error al cargar coaches: ${coachError.message || "Error del servidor"}`
          );
        }
      }

      try {
        classTypesList = await fetchClassTypes(sedeParams, sedeHeaders);
      } catch (classTypeError) {
        console.error("Error loading class types:", classTypeError);
        if (classTypeError.response?.status >= 400) {
          toast.error(
            `Error al cargar tipos de clase: ${classTypeError.message || "Error del servidor"}`
          );
        }
        classTypesList = [];
      }

      try {
        if (sedeId) {
          apiTimeSlots = await getTimeSlots(sedeId);
        }
      } catch (timeSlotError) {
        console.error("Error loading time slots:", timeSlotError);
        if (timeSlotError.response?.status >= 400) {
          toast.error(
            `Error al cargar slots de tiempo: ${timeSlotError.message || "Error del servidor"}`
          );
        }
        apiTimeSlots = [];
      }

      setSchedules(allSchedules);
      setCoaches(coachList);
      setClassTypes(classTypesList);
      setTimeSlots(mergeTimeSlots(apiTimeSlots, allSchedules));
      groupByDay(allSchedules);
      calculateStats(allSchedules, coachList);

      if (allSchedules.length > 0 || coachList.length > 0) {
        toast.success("Datos cargados correctamente");
      }
    } catch (error) {
      console.error("General error loading data:", error);
      if (
        error.response?.status >= 400 ||
        error.code === "NETWORK_ERROR" ||
        error.code === "ECONNABORTED"
      ) {
        setError(`Error al cargar datos: ${error.message || "Error desconocido"}`);
        toast.error(`Error al cargar datos: ${error.message || "Error desconocido"}`);
      }

      setSchedules([]);
      setCoaches([]);
      setClassTypes([]);
      setTimeSlots([]);
      setGrouped({});
      setStats({
        totalSchedules: 0,
        totalCoaches: 0,
        schedulesWithCoaches: 0,
        schedulesWithoutCoaches: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Escuchar cambios de sede y refrescar automáticamente
  useSedeRefresh(loadData, [addSedeParams, addSedeHeaders, selectedSedes]);

  // Refrescar datos cuando cambien las sedes seleccionadas
  useEffect(() => {
    if (selectedSedes.length > 0) {
      loadData();
    }
  }, [selectedSedes]);

  const calculateStats = (schedulesData, coachesData) => {
    const totalSchedules = schedulesData.length;
    const totalCoaches = coachesData.length;
    const schedulesWithCoaches = schedulesData.filter(s => s.coach).length;
    const schedulesWithoutCoaches = totalSchedules - schedulesWithCoaches;
    
    setStats({
      totalSchedules,
      totalCoaches,
      schedulesWithCoaches,
      schedulesWithoutCoaches
    });
  };

  const groupByDay = (data) => {
    const days = {};
    data.forEach((item) => {
      const key = dayMapping[item.day?.toUpperCase()] || item.day?.toLowerCase();
      if (!days[key]) days[key] = [];
      days[key].push(item);
    });
    Object.keys(days).forEach((day) => {
      days[day].sort((a, b) =>
        String(normalizeTimeSlot(a.time_slot) || "").localeCompare(
          String(normalizeTimeSlot(b.time_slot) || "")
        )
      );
    });
    setGrouped(days);
  };

  const handleCoachChange = async (scheduleId, newCoachId) => {
    try {
      const current = schedules.find((s) => s.id === scheduleId);
      const coach = newCoachId === "" || newCoachId == null ? null : parseInt(newCoachId, 10);
      const payload = buildSchedulePayload(current, { coach });
      const sedeParams = addSedeParams({});
      const sedeHeaders = addSedeHeaders({});

      setUpdatingId(scheduleId);
      await updateScheduleCoach(scheduleId, payload, sedeParams, sedeHeaders);
      toast.success("Coach actualizado correctamente");

      const updated = schedules.map((s) =>
        s.id === scheduleId ? { ...s, coach } : s
      );
      setSchedules(updated);
      groupByDay(updated);
      calculateStats(updated, coaches);
    } catch (error) {
      console.error("Error updating coach:", error);
      toast.error("Error al actualizar coach");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleCapacityChange = async (scheduleId, newCapacity) => {
    try {
      const current = schedules.find((s) => s.id === scheduleId);
      const capacity = parseInt(newCapacity, 10);
      const payload = buildSchedulePayload(current, { capacity });
      const sedeParams = addSedeParams({});
      const sedeHeaders = addSedeHeaders({});

      setUpdatingId(scheduleId);
      await updateScheduleCoach(scheduleId, payload, sedeParams, sedeHeaders);
      toast.success("Capacidad actualizada correctamente");

      const updated = schedules.map((s) =>
        s.id === scheduleId ? { ...s, capacity } : s
      );
      setSchedules(updated);
      groupByDay(updated);
      calculateStats(updated, coaches);
    } catch (error) {
      console.error("Error updating capacity:", error);
      toast.error("Error al actualizar capacidad");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedSchedules.length === 0) {
      toast.error("Selecciona al menos un horario");
      return;
    }

    if (!bulkCoach && !bulkCapacity) {
      toast.error("Selecciona al menos un campo para actualizar (coach o capacidad)");
      return;
    }

    try {
      setLoading(true);
      const sedeParams = addSedeParams({});
      const sedeHeaders = addSedeHeaders({});
      const promises = selectedSchedules.map((scheduleId) => {
        const current = schedules.find((s) => s.id === scheduleId);
        const overrides = {};
        if (bulkCoach) overrides.coach = parseInt(bulkCoach, 10);
        if (bulkCapacity) overrides.capacity = parseInt(bulkCapacity, 10);
        const payload = buildSchedulePayload(current, overrides);
        return updateScheduleCoach(scheduleId, payload, sedeParams, sedeHeaders);
      });

      await Promise.all(promises);
      toast.success(`${selectedSchedules.length} horarios actualizados correctamente`);

      const updated = schedules.map((s) => {
        if (selectedSchedules.includes(s.id)) {
          return {
            ...s,
            coach: bulkCoach ? parseInt(bulkCoach, 10) : s.coach,
            capacity: bulkCapacity ? parseInt(bulkCapacity, 10) : s.capacity,
          };
        }
        return s;
      });
      setSchedules(updated);
      groupByDay(updated);
      calculateStats(updated, coaches);
      setSelectedSchedules([]);
      setBulkCoach("");
      setBulkCapacity("");
      setShowBulkModal(false);
    } catch (error) {
      console.error("Error in bulk update:", error);
      toast.error("Error al actualizar horarios");
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleSelect = (scheduleId) => {
    setSelectedSchedules(prev => 
      prev.includes(scheduleId) 
        ? prev.filter(id => id !== scheduleId)
        : [...prev, scheduleId]
    );
  };

  const handleSelectAll = (daySchedules) => {
    const allSelected = daySchedules.every(s => selectedSchedules.includes(s.id));
    if (allSelected) {
      setSelectedSchedules(prev => 
        prev.filter(id => !daySchedules.some(s => s.id === id))
      );
    } else {
      setSelectedSchedules(prev => [
        ...prev,
        ...daySchedules.map(s => s.id).filter(id => !prev.includes(id))
      ]);
    }
  };

  const handleCreateSchedule = async () => {
    if (!newSchedule.day || !newSchedule.time_slot || !newSchedule.class_type_id) {
      toast.error("Por favor completa todos los campos obligatorios");
      return;
    }

    try {
      setLoading(true);
      const sedeParams = addSedeParams({});
      const sedeHeaders = addSedeHeaders({});
      const sedeId = resolveSedeId();

      const payload = {
        day: newSchedule.day,
        time_slot: normalizeTimeSlot(newSchedule.time_slot),
        class_type_id: parseInt(newSchedule.class_type_id, 10),
        coach: newSchedule.coach ? parseInt(newSchedule.coach, 10) : null,
        capacity: parseInt(newSchedule.capacity, 10),
        is_individual: newSchedule.is_individual,
      };
      if (sedeId) payload.sede_id = sedeId;

      await createScheduleAPI(payload, sedeParams, sedeHeaders);
      toast.success("Horario creado correctamente");

      setNewSchedule({
        day: "",
        time_slot: "",
        class_type_id: "",
        coach: "",
        capacity: 1,
        is_individual: false,
      });
      setShowCreateModal(false);
      loadData();
    } catch (error) {
      console.error("Error creating schedule:", error);
      toast.error("Error al crear horario");
    } finally {
      setLoading(false);
    }
  };

  const handleNewScheduleChange = (field, value) => {
    setNewSchedule(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const filteredSchedules = () => {
    let filtered = Object.entries(grouped);
    
    if (selectedDay) {
      filtered = filtered.filter(([day]) => day === selectedDay);
    }
    
    if (selectedClassType) {
      filtered = filtered.map(([day, schedules]) => [
        day, 
        schedules.filter(s => s.class_type?.id === parseInt(selectedClassType))
      ]).filter(([day, schedules]) => schedules.length > 0);
    }
    
    if (selectedCoach) {
      filtered = filtered.map(([day, schedules]) => [
        day,
        schedules.filter(s => s.coach === parseInt(selectedCoach))
      ]).filter(([day, schedules]) => schedules.length > 0);
    }
    
    if (searchTerm) {
      filtered = filtered.map(([day, schedules]) => [
        day,
        schedules.filter(s => 
          s.class_type?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          s.time_slot?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          coaches.find(c => Number(c.id) === Number(s.coach))?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          coaches.find(c => Number(c.id) === Number(s.coach))?.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      ]).filter(([day, schedules]) => schedules.length > 0);
    }
    
    return filtered;
  };

  const getCoachName = (coachId) => {
    if (coachId == null || coachId === "") return "Sin asignar";
    const coach = coaches.find((c) => Number(c.id) === Number(coachId));
    return coach ? `${coach.first_name} ${coach.last_name}` : "Sin asignar";
  };

  const getClassTypeColor = (classTypeName) => {
    if (!classTypeName) return "#6c757d";
    if (classTypeColors[classTypeName]) return classTypeColors[classTypeName];
    if (classTypeName.toLowerCase().includes("pilates")) return "#e74c3c";
    return "#6c757d";
  };

  const renderScheduleCard = (schedule) => {
    const isSelected = selectedSchedules.includes(schedule.id);
    const isUpdating = updatingId === schedule.id;
    const coachName = getCoachName(schedule.coach);
    const classTypeColor = getClassTypeColor(schedule.class_type?.name);

    return (
      <Col md="4" lg="3" key={schedule.id} className="mb-3">
        <Card 
          className={`h-100 ${isSelected ? 'border-primary' : ''} ${isUpdating ? 'opacity-75' : ''}`}
          style={{ 
            border: isSelected ? '2px solid #007bff' : '1px solid #dee2e6',
            transition: 'all 0.2s ease'
          }}
        >
          <CardHeader 
            className="d-flex justify-content-between align-items-center"
            style={{ backgroundColor: `${classTypeColor}15`, borderBottom: `2px solid ${classTypeColor}` }}
          >
            <div className="d-flex align-items-center">
              <CustomInput
                type="checkbox"
                id={`schedule-${schedule.id}`}
                checked={isSelected}
                onChange={() => handleScheduleSelect(schedule.id)}
                className="mr-2"
              />
              <div>
                <strong className="text-truncate d-block" style={{ maxWidth: '150px' }}>
                  {schedule.class_type?.name || "Sin tipo"}
                </strong>
                <small className="text-muted">
                  <i className="fa fa-clock mr-1"></i>
                  {schedule.time_slot}
                </small>
              </div>
            </div>
            <Badge color={schedule.coach ? "success" : "warning"} pill>
              {schedule.coach ? "Asignado" : "Sin asignar"}
            </Badge>
          </CardHeader>
          
          <CardBody className="p-3">
            <div className="mb-3">
              <FormGroup className="mb-2">
                <Label for={`capacity-${schedule.id}`} className="small font-weight-bold">
                  <i className="fa fa-users mr-1"></i>
                  Capacidad:
                </Label>
                <Input
                  type="number"
                  id={`capacity-${schedule.id}`}
                  value={schedule.capacity}
                  min="1"
                  max="50"
                  disabled={isUpdating}
                  onChange={(e) => handleCapacityChange(schedule.id, e.target.value)}
                  size="sm"
                />
              </FormGroup>
              
              <FormGroup className="mb-0">
                <Label for={`coach-${schedule.id}`} className="small font-weight-bold">
                  <i className="fa fa-graduation-cap mr-1"></i>
                  Coach:
                </Label>
                <Input
                  type="select"
                  id={`coach-${schedule.id}`}
                  value={schedule.coach || ""}
                  disabled={isUpdating}
                  onChange={(e) => handleCoachChange(schedule.id, e.target.value)}
                  size="sm"
                >
                  <option value="">Sin asignar</option>
                  {coaches.map((coach) => (
                    <option key={coach.id} value={coach.id}>
                      {coach.first_name} {coach.last_name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
              
              {isUpdating && (
                <div className="mt-2 text-center">
                  <Spinner size="sm" color="primary" />
                  <span className="ml-2 small text-muted">Actualizando...</span>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </Col>
    );
  };

  const renderStatsCards = () => (
    <Row className="mb-4">
      <Col md="3">
        <Card className="text-center border-0 shadow-sm">
          <CardBody>
            <i className="fa fa-calendar text-primary mb-2" style={{ fontSize: '2em' }}></i>
            <h4 className="mb-1">{stats.totalSchedules}</h4>
            <small className="text-muted">Total Horarios</small>
          </CardBody>
        </Card>
      </Col>
      <Col md="3">
        <Card className="text-center border-0 shadow-sm">
          <CardBody>
            <i className="fa fa-user text-success mb-2" style={{ fontSize: '2em' }}></i>
            <h4 className="mb-1">{stats.totalCoaches}</h4>
            <small className="text-muted">Total Coaches</small>
          </CardBody>
        </Card>
      </Col>
      <Col md="3">
        <Card className="text-center border-0 shadow-sm">
          <CardBody>
            <i className="fa fa-check text-info mb-2" style={{ fontSize: '2em' }}></i>
            <h4 className="mb-1">{stats.schedulesWithCoaches}</h4>
            <small className="text-muted">Con Coach</small>
          </CardBody>
        </Card>
      </Col>
      <Col md="3">
        <Card className="text-center border-0 shadow-sm">
          <CardBody>
            <i className="fa fa-exclamation-triangle text-warning mb-2" style={{ fontSize: '2em' }}></i>
            <h4 className="mb-1">{stats.schedulesWithoutCoaches}</h4>
            <small className="text-muted">Sin Coach</small>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );

  const renderFilters = () => (
    <Card className="mb-4">
      <CardBody>
        <Row>
          <Col md="3">
            <FormGroup>
              <Label for="dayFilter">Filtrar por día:</Label>
              <Input
                id="dayFilter"
                type="select"
                value={selectedDay}
                onChange={(e) => setSelectedDay(e.target.value)}
              >
                <option value="">-- Todos los días --</option>
                {Object.keys(diasSemana).map((day) => (
                  <option key={day} value={day}>
                    {diasSemana[day]}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          
          <Col md="3">
            <FormGroup>
              <Label for="classTypeFilter">Tipo de clase:</Label>
              <Input
                id="classTypeFilter"
                type="select"
                value={selectedClassType}
                onChange={(e) => setSelectedClassType(e.target.value)}
              >
                <option value="">-- Todos los tipos --</option>
                {classTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          
          <Col md="3">
            <FormGroup>
              <Label for="coachFilter">Coach:</Label>
              <Input
                id="coachFilter"
                type="select"
                value={selectedCoach}
                onChange={(e) => setSelectedCoach(e.target.value)}
              >
                <option value="">-- Todos los coaches --</option>
                {coaches.map((coach) => (
                  <option key={coach.id} value={coach.id}>
                    {coach.first_name} {coach.last_name}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          
          <Col md="3">
            <FormGroup>
              <Label for="searchInput">Buscar:</Label>
              <InputGroup>
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>
                    <i className="fa fa-search"></i>
                  </InputGroupText>
                </InputGroupAddon>
                <Input
                  id="searchInput"
                  placeholder="Buscar por clase, horario o coach..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </FormGroup>
          </Col>
        </Row>
        
        <Row className="mt-3">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                {selectedSchedules.length > 0 && (
                  <Badge color="primary" pill className="mr-2">
                    {selectedSchedules.length} seleccionados
                  </Badge>
                )}
                <Button
                  color="primary"
                  size="sm"
                  outline
                  onClick={() => setShowBulkModal(true)}
                  disabled={selectedSchedules.length === 0}
                >
                  <i className="fa fa-edit mr-1"></i>
                  Actualización masiva
                </Button>
              </div>
              
              <div>
                <Button
                  color="info"
                  size="sm"
                  outline
                  onClick={loadData}
                >
                  <i className="fa fa-refresh mr-1"></i>
                  Actualizar
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );

  if (loading) {
    return (
      <div style={overlayStyles}>
        <div style={loaderContainerStyles}>
          <ReactLoading type="spin" color="white" height="40%" />
        </div>
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" />
      
      {/* Componente de prueba temporal */}
      <TestCoachesEndpoint />
      
      <div className="content">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Gestión de Coaches en Horarios</h2>
            <p className="text-muted mb-0">Administra la asignación de coaches a los horarios de clases</p>
          </div>
          <div>
            <Button 
              color="success" 
              outline
              onClick={() => setShowCreateModal(true)}
              className="mr-2"
            >
              <i className="fa fa-plus mr-2"></i>
              Nuevo Horario
            </Button>
            {selectedSchedules.length > 0 && (
              <Button 
                color="primary" 
                onClick={() => setShowBulkModal(true)}
              >
                <i className="fa fa-edit mr-2"></i>
                Actualización Masiva ({selectedSchedules.length})
              </Button>
            )}
          </div>
        </div>

        {error && (
          <Alert color="danger" className="mb-4">
            <h5 className="alert-heading">Error al cargar datos</h5>
            <p className="mb-0">{error}</p>
            <hr />
            <div className="d-flex justify-content-between align-items-center">
              <small>Verifica la consola del navegador para más detalles</small>
              <Button color="danger" size="sm" onClick={loadData}>
                <i className="fa fa-refresh mr-1"></i>
                Reintentar
              </Button>
            </div>
          </Alert>
        )}
        
        {renderStatsCards()}
        {renderFilters()}

        <Row>
          {filteredSchedules().map(([day, daySchedules]) => (
            <Col md="12" key={day} className="mb-4">
              <Card className="border-0 shadow-sm">
                <CardHeader 
                  className="bg-light d-flex justify-content-between align-items-center"
                  style={{ borderBottom: '2px solid #e9ecef' }}
                >
                  <div className="d-flex align-items-center">
                    <CustomInput
                      type="checkbox"
                      id={`select-all-${day}`}
                      checked={daySchedules.every(s => selectedSchedules.includes(s.id))}
                      onChange={() => handleSelectAll(daySchedules)}
                      className="mr-3"
                    />
                    <h5 className="mb-0 text-uppercase">
                      {diasSemana[day] || day}
                    </h5>
                    <Badge color="secondary" className="ml-2">
                      {daySchedules.length} horarios
                    </Badge>
                  </div>
                </CardHeader>
                
                <CardBody className="p-3">
                  <Row>
                    {daySchedules.map(renderScheduleCard)}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        {filteredSchedules().length === 0 && !loading && (
          <Card className="text-center py-5">
            <CardBody>
              <i className="fa fa-calendar text-muted mb-3" style={{ fontSize: '3em' }}></i>
              <h5>
                {schedules.length === 0 ? "No hay horarios disponibles" : "No se encontraron horarios"}
              </h5>
              <p className="text-muted">
                {schedules.length === 0 
                  ? "No hay horarios configurados para esta sede. Contacta al administrador para crear horarios."
                  : "Intenta ajustar los filtros para ver más resultados"
                }
              </p>
              {schedules.length === 0 && (
                <div className="mt-3">
                  <Button color="primary" onClick={loadData} className="mr-2">
                    <i className="fa fa-refresh mr-1"></i>
                    Recargar datos
                  </Button>
                  <Button color="info" outline onClick={() => window.location.reload()}>
                    <i className="fa fa-refresh mr-1"></i>
                    Recargar página
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>
        )}
      </div>

      {/* Bulk Update Modal */}
      <Modal isOpen={showBulkModal} toggle={() => setShowBulkModal(false)} size="md">
        <ModalHeader toggle={() => setShowBulkModal(false)}>
          Actualización Masiva
        </ModalHeader>
        <ModalBody>
          <Alert color="info">
            <strong>{selectedSchedules.length}</strong> horarios seleccionados
          </Alert>
          
          <FormGroup>
            <Label for="bulkCoachSelect">Coach (opcional):</Label>
            <Input
              type="select"
              id="bulkCoachSelect"
              value={bulkCoach}
              onChange={(e) => setBulkCoach(e.target.value)}
            >
              <option value="">Mantener coach actual</option>
              {coaches.map((coach) => (
                <option key={coach.id} value={coach.id}>
                  {coach.first_name} {coach.last_name}
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup>
            <Label for="bulkCapacityInput">Capacidad (opcional):</Label>
            <Input
              type="number"
              id="bulkCapacityInput"
              value={bulkCapacity}
              min="1"
              max="50"
              placeholder="Mantener capacidad actual"
              onChange={(e) => setBulkCapacity(e.target.value)}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowBulkModal(false)}>
            Cancelar
          </Button>
          <Button 
            color="primary" 
            onClick={handleBulkUpdate}
            disabled={!bulkCoach && !bulkCapacity}
          >
            <i className="fa fa-save mr-1"></i>
            Actualizar Horarios
          </Button>
        </ModalFooter>
      </Modal>

      {/* Create Schedule Modal */}
      <Modal isOpen={showCreateModal} toggle={() => setShowCreateModal(false)} size="lg">
        <ModalHeader toggle={() => setShowCreateModal(false)}>
          Crear Nuevo Horario
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="newDay">Día de la semana *</Label>
                <Input
                  type="select"
                  id="newDay"
                  value={newSchedule.day}
                  onChange={(e) => handleNewScheduleChange('day', e.target.value)}
                >
                  <option value="">Seleccionar día</option>
                  <option value="MON">Lunes</option>
                  <option value="TUE">Martes</option>
                  <option value="WED">Miércoles</option>
                  <option value="THU">Jueves</option>
                  <option value="FRI">Viernes</option>
                  <option value="SAT">Sábado</option>
                  <option value="SUN">Domingo</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="newTimeSlot">Horario *</Label>
                <Input
                  type="select"
                  id="newTimeSlot"
                  value={newSchedule.time_slot}
                  onChange={(e) => handleNewScheduleChange('time_slot', e.target.value)}
                  disabled={timeSlots.length === 0}
                >
                  <option value="">
                    {timeSlots.length === 0
                      ? "No hay time slots activos"
                      : "Seleccionar horario"}
                  </option>
                  {timeSlots.map(slot => (
                    <option key={slot.id} value={slot.time_slot_value}>
                      {slot.time_slot_display}
                    </option>
                  ))}
                </Input>
                {timeSlots.length === 0 && (
                  <small className="text-muted">
                    Revisa Clases → Gestión de Horarios o espera el sync de TimeSlots.
                  </small>
                )}
              </FormGroup>
            </Col>
          </Row>
          
          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="newClassType">Tipo de Clase *</Label>
                <Input
                  type="select"
                  id="newClassType"
                  value={newSchedule.class_type_id}
                  onChange={(e) => handleNewScheduleChange('class_type_id', e.target.value)}
                >
                  <option value="">Seleccionar tipo</option>
                  {classTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="newCoach">Coach (opcional)</Label>
                <Input
                  type="select"
                  id="newCoach"
                  value={newSchedule.coach}
                  onChange={(e) => handleNewScheduleChange('coach', e.target.value)}
                >
                  <option value="">Sin asignar</option>
                  {coaches.map(coach => (
                    <option key={coach.id} value={coach.id}>
                      {coach.first_name} {coach.last_name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>
          
          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="newCapacity">Capacidad</Label>
                <Input
                  type="number"
                  id="newCapacity"
                  value={newSchedule.capacity}
                  onChange={(e) => handleNewScheduleChange('capacity', e.target.value)}
                  min="1"
                  max="50"
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <div className="custom-control custom-checkbox">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="newIsIndividual"
                    checked={newSchedule.is_individual}
                    onChange={(e) => handleNewScheduleChange('is_individual', e.target.checked)}
                  />
                  <Label className="custom-control-label" for="newIsIndividual">
                    Clase individual
                  </Label>
                </div>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary"  outline onClick={() => setShowCreateModal(false)}>
            Cancelar
          </Button>
          <Button 
            color="success" 
            outline
            onClick={handleCreateSchedule}
            disabled={loading}
          >
            <i className="fa fa-plus mr-1"></i>
            Crear Horario
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default CoachScheduleManager;
